import { createContext, useContext, useEffect, useState } from 'react';

/**
 * ConnectionContext — single source of truth for the browser's network state.
 *
 * Driven exclusively by `navigator.onLine` + the `online`/`offline` window
 * events (no polling, no external library, per the spec).
 *
 * Consumers:
 *   - <ConnectionStatusBar/>      visible banner / online dot in MainLayout
 *   - Shadcn <Button>             gates `type=submit` and `data-mutating` buttons
 *   - axios request interceptor   defense in depth — refuses writes while offline
 */
const ConnectionContext = createContext({ isOnline: true });

// Standalone state for non-React consumers (e.g., the axios interceptor in
// AuthContext.js). The hook below keeps this in sync.
let _isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
const _subs = new Set();

export function getIsOnline() {
  return _isOnline;
}

function _setOnline(next) {
  if (_isOnline === next) return;
  _isOnline = next;
  _subs.forEach((cb) => cb(next));
}

export const ConnectionProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(_isOnline);

  useEffect(() => {
    const handleOnline = () => _setOnline(true);
    const handleOffline = () => _setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Keep React state in sync with the standalone store.
    const sub = (next) => setIsOnline(next);
    _subs.add(sub);

    // Re-read at mount — covers the case where the tab was offline before
    // the provider mounted.
    _setOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      _subs.delete(sub);
    };
  }, []);

  return (
    <ConnectionContext.Provider value={{ isOnline }}>
      {children}
    </ConnectionContext.Provider>
  );
};

export const useConnection = () => useContext(ConnectionContext);
